/**
 * useCriticalLogos — Batch-fetch logos for currently visible symbols.
 *
 * Usage:
 *   const symbols = stocks.map(s => s.symbol);
 *   useCriticalLogos(symbols);
 *
 * On mount (or when symbols change), warms the in-memory cache from
 * IndexedDB (instant) then fires a batch request for any remaining misses.
 */
import { useEffect, useRef } from 'react';
import { requestLogos, warmFromIDB } from '@/lib/logoPipeline';

export function useCriticalLogos(symbols, { critical = true } = {}) {
  const prevKey = useRef('');

  useEffect(() => {
    if (!symbols || symbols.length === 0) return;

    // Dedupe: only re-fetch if the symbol set actually changed
    const key = symbols.slice(0, 100).sort().join(',');
    if (key === prevKey.current) return;
    prevKey.current = key;

    // Warm from IndexedDB first (instant for previously-seen symbols),
    // then fetch any remaining from the network
    warmFromIDB(symbols).finally(() => {
      requestLogos(symbols, { critical });
    });
  }, [symbols, critical]);
}
